<template>
  <div :class="rootClasses">
    <div :class="['real-wish', { 'vertically-centered': verticallyCenterContent }]" @contextmenu="tryShowActions">
      <!-- This out-of-bounds (oob) h3 acts as a title for posts that don't have one, for consistency. -->
      <h3 class="accessibility oob" tabindex="0">{{ headline }}</h3>
      <surface-post-author v-if="xAuthor" :post="post" :user="user" class="author" />
      <div v-if="isWishContentScrollable" class="wish-content">
        <section v-if="xWords" :class="['words', { 'vertically-centered': verticallyCenterContent }]">
          <surface-post-words
            :post="post"
            :is-editable-by-current-user="isEditableByCurrentUser"
            @edit="startEditingPost({ postCid: post.cid })"
          />
        </section>
        <surface-post-attachment
          v-if="xAttachment"
          :post="post"
          :is-editable-by-current-user="isEditableByCurrentUser"
        />
      </div>
      <template v-else>
        <section v-if="xWords" :class="['words', { 'vertically-centered': verticallyCenterContent }]">
          <surface-post-words
            :post="post"
            :is-editable-by-current-user="isEditableByCurrentUser"
            @edit="startEditingPost({ postCid: post.cid })"
          />
        </section>
        <surface-post-attachment
          v-if="xAttachment"
          :post="post"
          :is-editable-by-current-user="isEditableByCurrentUser"
        />
      </template>
      <surface-post-moderation
        v-if="xModeration"
        :post="post"
        :can-i-moderate="canIModerate"
        :is-being-edited="false"
        @approve="approvePost({ postCid: post.cid })"
        @delete="confirmDelete"
      />
      <section v-if="xComments || xReactions" class="stalker-bar">
        <surface-post-reaction
          v-if="xReactions"
          :post="post"
          :is-reacting="isBeingReacted"
          :is-reactable="isReactable"
          :user-id="user.id"
          :reaction-data="reactionData"
          :reaction-ids="postReactionIds"
          :reactions="wallReactions"
        />
        <surface-post-comments
          v-if="xComments && !xPostCommentsInPanel"
          :comment-being-edited-id="commentBeingEditedId"
          :post="post"
          :user="user"
          :comment-ids="postCommentIds"
          :comments="wallComments"
          :is-commentable="isCommentable"
          @create="createComment($event)"
          @show-comment-action-menu="showCommentActionMenu($event)"
          @stop-editing="stopEditingComment"
          @edit="editComment($event)"
        />
        <div v-if="!xReactions && xPostCommentsInPanel" />
        <!-- keep comment icon on the right when there is no reaction button-->
        <surface-post-comments-icon
          v-if="xComments && xPostCommentsInPanel"
          :comment-ids="postCommentIds"
          @show-comments-panel="showCommentsPanel({ postId: post.id })"
        />
      </section>
    </div>
    <surface-post-actions
      :x-author="xAuthor"
      :post="post"
      :attachment-luminance="attachmentLuminance"
      :color-scheme="colorScheme"
      :is-editable-by-current-user="isEditableByCurrentUser"
      :is-being-edited="false"
      @delete="confirmDelete"
      @edit="startEditingPost({ postCid: post.cid })"
      @dropdown="showPostActionMenu({ postCid: post.cid })"
    />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapActions, mapGetters } from 'vuex'
import SurfacePostAuthor from 'vuecomponents/surface_post_author'
import SurfacePostWords from 'vuecomponents/surface_post_words'
import SurfacePostActions from 'vuecomponents/surface_post_actions'
import SurfacePostAttachment from 'vuecomponents/surface_post_attachment'
import SurfacePostModeration from 'vuecomponents/surface_post_moderation'
import SurfacePostReaction from 'vuecomponents/surface_post_reaction'
import SurfacePostComments from 'vuecomponents/surface_post_comments'
import SurfacePostCommentsIcon from 'vuecomponents/surface_post_comments_icon'
import IntlMixin from 'vuemixins/intl_mixin'
import { observeIntersection, canObserveIntersection } from 'bits/intersection'
import { isUrl } from 'bits/url'
import { $ } from 'bits/jquery'

export default {
  components: {
    SurfacePostAuthor,
    SurfacePostWords,
    SurfacePostAttachment,
    SurfacePostActions,
    SurfacePostModeration,
    SurfacePostReaction,
    SurfacePostComments,
    SurfacePostCommentsIcon,
  },
  mixins: [IntlMixin],
  props: {
    index: { type: Number, default: null },
    post: { type: Object, default: () => ({}) },
    edit: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    fitHeightToContainer: { type: Boolean, default: false },
    verticallyCenterContent: { type: Boolean, default: false },
  },
  data: function () {
    return {
      // check if this post has intersected the viewport at least once
      hasIntersectedViewport: !canObserveIntersection,
    }
  },
  computed: {
    ...mapState({
      user: 'user',
      isMobile: 'isMobile',
    }),
    ...mapState('reaction', {
      wallReactions: 'reactionEntities',
      postIdBeingReactedTo: 'postIdBeingReactedTo',
    }),
    ...mapState('comment', {
      commentBeingEditedId: 'commentBeingEditedId',
      wallComments: 'commentEntities',
    }),
    ...mapState('postConnection', ['postToConnectFromId']),
    ...mapGetters([
      'xAuthor',
      'reactionData',
      'isCommentable',
      'isReactable',
      'isTimeline',
      'canIModerate',
      'canIWrite',
      'xPostCommentsInPanel',
      'colorScheme',
    ]),
    ...mapGetters('reaction', {
      wallReactionIdsByPost: 'reactionIdsByPost',
    }),
    ...mapGetters('comment', {
      wallCommentIdsByPost: 'commentIdsByPost',
    }),
    ...mapGetters('attachment', ['linkDisplayAttributesForUrl']),
    isPublished() {
      return this.post.published
    },
    subject() {
      return this.post.subject
    },
    body() {
      return this.post.body
    },
    headline() {
      return this.post.headline
    },
    isBeingReacted() {
      return this.postIdBeingReactedTo === this.post.id
    },
    hasAttachment() {
      const attachment = this.post.attachment
      return !!attachment && isUrl(attachment)
    },
    xWords() {
      return this.subject || this.body
    },
    xAttachment() {
      return this.hasAttachment && this.hasIntersectedViewport
    },
    xComments() {
      // Turn comments off when switching to Backchannel
      return this.isPublished && (this.isCommentable || this.postCommentIds.length > 0)
    },
    xModeration() {
      return !this.isPublished
    },
    xReactions() {
      // when a user is viewing a wall with reaction disabled and then reaction is enabled + someone react to something
      // the realtime message for new reaction may come before the reaction enable message
      // => wait for reactionData to be available before showing reactions
      return (this.isReactable || this.postReactionIds.length > 0) && this.reactionData
    },
    xConnect() {
      return (
        !!this.postToConnectFromId &&
        this.postToConnectFromId !== this.post.id &&
        !this.areConnected(this.post.id, this.postToConnectFromId)
      )
    },
    isWishContentScrollable() {
      return this.isTimeline
    },
    postCommentIds() {
      return this.wallCommentIdsByPost[this.post.id] || []
    },
    postReactionIds() {
      return this.wallReactionIdsByPost[this.post.id] || []
    },
    attachmentLuminance() {
      if (!this.post.attachment) return null
      const attributes = this.linkDisplayAttributesForUrl(this.post.attachment)
      return (attributes && attributes.image_luminance) || null
    },
    wasWrittenByCurrentUser() {
      return !!this.user.id && this.post.author_id === this.user.id
    },
    // Either the current user is a moderator or current user is the author and wall is writable
    isEditableByCurrentUser() {
      return this.canIModerate || (this.wasWrittenByCurrentUser && this.canIWrite)
    },
    rootClasses() {
      return {
        'wish-frame': true,
        'full-height': this.fitHeightToContainer,
      }
    },
  },
  methods: {
    ...mapActions(['showCommentsPanel']),
    ...mapActions('post', ['startEditingPost', 'approvePost', 'deletePost']),
    ...mapActions('postAction', ['showPostActionMenu']),
    ...mapActions('comment', [
      'createComment',
      'editComment',
      'stopEditingComment',
      'startEditingComment',
      'showCommentActionMenu',
    ]),
    areConnected(post1Id, post2Id) {
      return (
        this.connections.filter((c) => {
          return (
            (c.from_wish_id === post1Id && c.to_wish_id === post2Id) ||
            (c.from_wish_id === post2Id && c.to_wish_id === post1Id)
          )
        }).length > 0
      )
    },
    confirmDelete() {
      if (
        !this.post.id /* post is new */ ||
        (!this.post.body && !this.post.subject && !this.post.attachment) ||
        confirm(this.__('Are you sure you want to delete this post? This cannot be undone!'))
      ) {
        this.deletePost({ postCid: this.post.cid })
      }
    },
    tryShowActions(e) {
      if (this.isMobile || this.xConnect || $(e.target).is('a,textarea,input,select,option') || e.shiftKey) {
        return
      }
      e.preventDefault()
      this.showPostActionMenu({ postCid: this.post.cid })
    },
  },
  mounted() {
    if (!this.hasIntersectedViewport) {
      observeIntersection(this.$el, () => {
        this.hasIntersectedViewport = true
      })
    }
  },
}
</script>

<style lang="scss" scoped>
.stalker-bar {
  html[data-layout='map'] &,
  html[data-layout='free'] &,
  html[data-layout='timeline'] & {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    background-color: rgba(black, 0.04);
    height: 34px;
    min-height: 34px; //safari
    .surface-post-reaction {
      background-color: transparent !important;
    }
    .surface-post-comments {
      background-color: transparent !important;
    }
  }
  html > body[data-color-scheme='dark'] & {
    background-color: rgba(black, 0.06);
  }
}

.full-height {
  height: 100%;

  .real-wish {
    height: 100%;
  }
}
.wish-content {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: auto;
  .surface-post-attachment {
    overflow: visible;
  }
  .words {
    overflow: visible;
  }
}

.vertically-centered {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
}
</style>
